import { useTranslator } from "@eo-locale/react";

import { string } from "yup";

import NextLink from "next/link";

import {
  Field,
  InputGroup,
  IconButton,
  Input,
  useBoolean,
  IoEyeOffOutline,
  IoEyeOutline,
  VisuallyHidden
} from "@/storybook-components";
import type { ChangeEvent } from "react";
import type { Question } from "@/types";
import { setAnswer } from "@/redux/features/questions/slice";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";

interface LoginPasswordQuestionProps {
  question: Partial<Question>;
}

export const validate = async (
  answer: string,
  question: Partial<Question>
): Promise<string[]> => {
  const schema = string().required();
  try {
    await schema.validate(answer);
    return [];
  } catch (err) {
    return err.errors;
  }
};

const LoginPasswordQuestion = ({
  question
}: LoginPasswordQuestionProps): JSX.Element => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const [revealPwd, setRevealPwd] = useBoolean();
  const { answers, errors, status } = useAppSelector(
    (state) => state.questions
  );

  const error = errors[question.id] || [];
  const answer = answers[question.id] || "";

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    dispatch(
      setAnswer({
        questionId: question.id,
        answer: e.target.value
      })
    );
  };

  return (
    <Field.Root
      my={4}
      id={`${question.id}`}
      invalid={error.length > 0}
      required={question.required}
      disabled={status === "saving"}
      className={error.length > 0 ? "ehqQuestionError" : null}
    >
      <Field.Label>
        {translate("questions.login.password")}
        {question.required && (
          <VisuallyHidden>{`* ${translate("common.required")}`}</VisuallyHidden>
        )}
      </Field.Label>
      <InputGroup
        endElement={
          <IconButton
            size="sm"
            fontSize="lg"
            variant="unstyled"
            display="inline-flex"
            onClick={setRevealPwd.toggle}
            icon={revealPwd ? <IoEyeOutline /> : <IoEyeOffOutline />}
            aria-label={
              revealPwd ? translate("common.hide") : translate("common.show")
            }
            data-testid="login-participant-button-password-toggle"
          />
        }
        endElementProps={{ width: 10, height: "100%" }}
      >
        <Input
          pr={10}
          value={answer}
          autoComplete="off"
          onChange={handleChange}
          type={revealPwd ? "text" : "password"}
          data-testid="loginPasswordQuestion-input"
          size={{ base: "lg", lg: "md" }}
          name="loginPasswordQuestion-input"
          autoCorrect="off"
        />
      </InputGroup>
      {error.length > 0 && (
        <Field.ErrorText data-testid="loginPasswordQuestion-errorMessage">
          <Field.ErrorIcon />
          {translate(`errors.${error}`)}
        </Field.ErrorText>
      )}
      <Field.HelperText data-testid="forgotPassword-link">
        <NextLink href="/users/password/new" passHref>
          {translate("questions.login.forgotPassword")}
        </NextLink>
      </Field.HelperText>
    </Field.Root>
  );
};

export default LoginPasswordQuestion;
